import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarClock, Sparkles, Clock, ShieldCheck, Check, ChevronDown } from "lucide-react";
import useDocumentMeta from "@/lib/useDocumentMeta";

const INTRO =
    "Професионално цялостно почистване на автомобила с премиум продуктите на Koch-Chemie";

const PACKAGES = [
    {
        title: "VIP комплексна автомивка",
        duration: "120 мин",
        intro: INTRO,
        bullets: [
            "Безопасно измиване без риск от микронадрасквания",
            "Дълбок блясък на боята",
            "Чист и освежен интериор",
            "Защитени пластмаси и гуми",
            "Дълготрайно усещане за нов автомобил",
        ],
    },
    {
        title: "VIP автомивка + интериорен детайлинг",
        duration: "5 часа",
        intro: INTRO,
        bullets: [
            "Многоетапно външно измиване с pH-неутрални препарати и защитен хидрофобен финиш.",
            "Детайлно почистване на джанти, гуми, арки и стъклени повърхности.",
            "Цялостно прахосмучене на купето и багажното отделение.",
            "Екстракционно пране на седалки, мокети и текстилни повърхности.",
            "Почистване на пластмаси, винил и декоративни елементи с UV защита.",
            "Детайлно обработване на табло, конзола, вентилационни отвори, кори на вратите и труднодостъпни зони.",
            "Финален контрол на качеството и ароматизиране на интериора.",
        ],
    },
];

export default function DetailingPage() {
    useDocumentMeta({
        title: "Детайлинг — Запазване на час | Konstantin Mobile",
        description:
            "Запишете час за професионален детайлинг (външно, вътрешно, комплексно) в Konstantin Mobile, Петрич. Онлайн резервация с автоматично изчисляване на свободни часове.",
        path: "/services/detailing",
    });
    const [expanded, setExpanded] = useState({});
    const cardRefs = useRef({});
    const toggle = (i) => {
        const willOpen = !expanded[i];
        setExpanded((s) => ({ ...s, [i]: willOpen }));
        if (willOpen) {
            // Wait for the framer-motion expand to finish before centering
            setTimeout(() => {
                cardRefs.current[i]?.scrollIntoView({ block: "center", behavior: "smooth" });
            }, 380);
        }
    };

    return (
        <main className="relative pt-32 pb-24 min-h-screen" data-testid="detailing-page">
            <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-ink-muted hover:text-gold text-sm mb-8"
                >
                    <ArrowLeft size={16} /> Назад към сайта
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="mb-10"
                >
                    <span className="block text-gold text-[11px] uppercase tracking-[0.32em] mb-4">
                        Услуги · Детайлинг
                    </span>
                    <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">
                        Записване за детайлинг
                    </h1>
                    <p className="text-ink-muted max-w-2xl">
                        Изберете удобен ден и час за Вас — нашият екип ще се погрижи за всеки детайл от Вашия автомобил.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-6 mb-10">
                    {PACKAGES.map((p, idx) => {
                        const isOpen = !!expanded[idx];
                        return (
                            <div
                                key={p.title}
                                ref={(el) => { cardRefs.current[idx] = el; }}
                                className="p-7 rounded-2xl bg-surface border border-line shadow-soft flex flex-col"
                                data-testid={`detailing-package-${idx}`}
                            >
                                <Sparkles size={20} className="text-gold mb-4" />
                                <div className="font-display text-xl text-ink leading-snug min-h-[3.5rem]">{p.title}</div>
                                <div className="text-xs text-ink-muted mt-1.5 flex items-center gap-1">
                                    <Clock size={12} /> {p.duration}
                                </div>
                                <p className="text-sm text-ink-muted mt-4 leading-relaxed">{p.intro}</p>

                                <div className="text-[11px] uppercase tracking-[0.25em] text-gold mt-5">
                                    Процедурата включва:
                                </div>

                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.ul
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                            className="overflow-hidden mt-4 space-y-2"
                                            data-testid={`detailing-bullets-${idx}`}
                                        >
                                            {p.bullets.map((b, i) => (
                                                <li key={i} className="flex items-start gap-2.5 text-sm text-ink leading-relaxed">
                                                    <Check size={15} className="text-gold shrink-0 mt-0.5" />
                                                    <span>{b}</span>
                                                </li>
                                            ))}
                                        </motion.ul>
                                    )}
                                </AnimatePresence>

                                <button
                                    type="button"
                                    onClick={() => toggle(idx)}
                                    data-testid={`detailing-toggle-${idx}`}
                                    className="mt-5 inline-flex items-center gap-1.5 self-start text-sm text-gold hover:text-gold-light transition-colors"
                                    aria-expanded={isOpen}
                                >
                                    {isOpen ? "Скрий" : "Виж повече"}
                                    <ChevronDown
                                        size={14}
                                        className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
                                    />
                                </button>
                            </div>
                        );
                    })}
                </div>

                <div
                    className="bg-surface border border-line rounded-3xl p-8 lg:p-10 shadow-soft"
                    data-testid="detailing-appointment"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/20 text-gold flex items-center justify-center">
                            <CalendarClock size={20} />
                        </div>
                        <div>
                            <h2 className="font-display text-2xl">Онлайн резервация</h2>
                            <p className="text-sm text-ink-muted">
                                Автоматично изчисляване на свободни часове. Потвърждение по имейл.
                            </p>
                        </div>
                    </div>

                    <ul className="text-sm text-ink-muted space-y-2 mb-8">
                        <li className="flex items-center gap-2">
                            <ShieldCheck size={14} className="text-gold" />
                            Работно време: Пон – Пет, 09:00 – 18:00
                        </li>
                        <li className="flex items-center gap-2">
                            <ShieldCheck size={14} className="text-gold" />
                            Потвърждение с Google Calendar покана
                        </li>
                    </ul>

                    <Link
                        to="/services/booking?category=detailing"
                        data-testid="detailing-book-cta"
                        className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-champagne px-8 py-4 rounded-full text-sm font-medium shadow-gold transition-all"
                    >
                        Резервирай онлайн <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        </main>
    );
}
